/**
 * Compliance rail 
 * ---------------------------------
 * The docked overlay on the EMR page: 44px at the right edge, holding the
 * Comprehend mark and a compliance grade. First view of what becomes the host
 * shell — the mark is here now so the rail reads as infrastructure rather than
 * as a grading widget that appears and disappears.
 *
 * Per ADR-001 the rail does NOT free-float: fixed width, fixed edge, three
 * vertical stops. It never reflows the host EMR and never solicits attention —
 * no pulse, no auto-open, no toast on score change.
 *
 * Interaction:
 *   mark     score / re-score this note
 *   grade    open the review modal
 *   chevron  open the real Comprehend side panel, and close it again
 *   grade    a fixed, tinted chip under the chevron: the rail's one action
 *            (grade this note / open the review), so it never moves and never
 *            looks like the navigation buttons below it.
 *   ribbon   one button per pinned item, in the clinician's order — the same
 *            items and order as the visit view's tabs (Note, Chat, pinned
 *            apps; Compliance has the chip above), then Apps. Each opens the
 *            side panel on its tab. Right-click → Edit layout to rearrange;
 *            the tabs follow (see Workspace layout below).
 *   drag     anywhere on the rail, between three vertical stops
 *   hover    peek panel, opening left
 *   tray     7px sliver; click or Cmd/Ctrl+Shift+C restores. Never unmounts.
 *
 * Vanilla DOM on purpose: this ships inside the content script (`content.js`),
 * which has no React root. Credentials never come near it — the background
 * worker holds the session and makes every API call.
 */

import {
  ensureComplianceSpinnerStyle,
  hideComplianceModal,
  showComplianceModal,
  complianceGapCount,
  type ComplianceReason
} from './complianceModal.ts';
import { needsSidebarPlacement, sidebarTabTop } from './railPlacement.ts';

/* -------------------------------------------------------------------------- */
/* Design tokens — from the vertical-pill spec                                */
/* -------------------------------------------------------------------------- */

const SURFACE = '#F7F9FA';
const SHADOW =
  '0 1px 2px rgba(14,17,22,.06),0 8px 22px -12px rgba(14,17,22,.35)';
const LINE = '#DDE2E8';
const LINE2 = '#EFF1F4';
const MUTED = '#737C89';
const INK = '#0E1116';
const INK2 = '#363C46';
const CHEV_INK = '#5B6572';
const CHEV_HOVER = '#EAEEF1';
// Ribbon hover: a filled rounded square, dark enough to read as "this is a
// button" at a glance — the old 30px CHEV_HOVER circle was barely there.
const BUTTON_HOVER = '#DFE5EC';
// The button for whatever the side panel has open: a ring in the tab bar's
// own blue (.custom-tabs .nav-link.active, #007bff), so the mark and the tab
// underline read as one thing. A ring, not a fill, so it means the same on
// every button, the tinted grade chip included.
const ACTIVE_INK = '#0069D9';
const ACTIVE_RING = '#007BFF';
const BUTTON_SIZE = 32;
const BUTTON_RADIUS = 9;
// Compliance's tooltip is a card, not a pill: green, because it's the one
// button whose click does something (grades the note) rather than opens a tab.
const TIP_COMPLIANCE_BG = '#E9F8F1';
// The grade chip's fill, by grade — the letter wears railGradeColor on it.
const CHIP_TINT: Record<string, { bg: string; line: string; hover: string }> = {
  none: { bg: '#E9F8F1', line: '#BFE6D3', hover: '#D6F1E4' },
  A: { bg: '#E9F8F1', line: '#BFE6D3', hover: '#D6F1E4' },
  B: { bg: '#FDF3E1', line: '#F2D9A6', hover: '#FBEACB' },
  C: { bg: '#FDF3E1', line: '#F2D9A6', hover: '#FBEACB' },
  F: { bg: '#FCEAE9', line: '#F2C2BF', hover: '#F9DAD8' }
};
const TIP_COMPLIANCE_LINE = '#BFE6D3';
const BRAND = '#2DCE91';

const RISK = '#B4231C';
const WATCH = '#A96A05';
const PASS = '#14724F';

// The mockup is set in IBM Plex. We can't pull a webfont into a customer's EMR
// (their CSP, their network), so the rail uses system stacks — the grade is
// tabular-ish mono either way, which is all the type is doing here.
const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
const SANS = "system-ui, -apple-system, 'Segoe UI', sans-serif";

const RAIL_WIDTH = 44;
// The embed's capsule radius (cx_embed loader.js: var radius = '10px'), rounded
// only on the corners facing the page -- the edge-facing pair stays square.
const EDGE_RADIUS = 10;
// The mark is the rail's identity, so it gets the width: 44 less ~7px either side.
const MARK_SIZE = 30;
// The Comprehend logo (src/assets/img/brand/logo.png), resized 512 -> 90px
// so it stays crisp at 3x while costing ~11KB rather than ~101KB, and inlined as
// a data URI so the rail never makes a network request into a customer's EMR.
const MARK_PNG =
  'data:image/png;base64,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';
const TRAY_WIDTH = 7;
const TRAY_HEIGHT = 64;
const PEEK_WIDTH = 194;
const Z = 2147483645;

const PEEK_IN_MS = 150;
const PEEK_OUT_MS = 250;
const NOTE_POLL_MS = 2500;

const ROOT_ID = 'comprehendpt-compliance-rail';
const EMBED_ROOT_ID = 'comprehendpt-embed-compliance-rail';
const TOP_KEY = 'comprehendpt_rail_top';
const TRAYED_KEY = 'comprehendpt_rail_trayed';
// Retired keys from the free-floating build, cleared on mount so nobody is
// left with an invisible rail or a stale XY position.
const RETIRED_KEYS = [
  'comprehendpt_score_bubble_pos',
  'comprehendpt_score_bubble_size',
  'comprehendpt_score_bubble_collapsed',
  'comprehendpt_score_bubble_dismissed',
  // Superseded by free vertical placement (TOP_KEY).
  'comprehendpt_rail_stop'
];

/** Keep this far clear of the top and bottom of the viewport, as the embed does. */
const EDGE_MARGIN = 8;
/** Space kept between an unplaced rail's bottom and the top of Stride's chat tab. */
const SIDEBAR_TAB_GAP = 12;
/**
 * Stride draws its chat tab centred on the window, but only on some pages (and
 * after load). Until it can be measured, assume it is there, about this tall,
 * so the rail never starts out sitting where the tab will appear.
 */
const SIDEBAR_TAB_ESTIMATED_HEIGHT = 64;

let started = false;
let watchingSetting = false;
// The Compliance Rail setting (chrome.storage.sync), shared with Note
// Preferences, the side panel's edit mode and the rail's own menu.
const RAIL_SETTING_KEY = 'complianceRail';

/* -------------------------------------------------------------------------- */
/* Injector shapes                                                            */
/* -------------------------------------------------------------------------- */

type RailInjector = {
  // The note-capture trio: cleaner values, but only Clinicient, CORA,
  // MWTherapy and the two WebPT injectors implement it.
  getPatientInfoForCapture?: () => any;
  getPageInfoForCapture?: () => any;
  getNoteKeyForCapture?: () => string | null | Promise<string | null>;
  getQuestionsForCapture?: () => Record<string, any> | Promise<Record<string, any>>;
  // The generic trio, which every EMR injector implements — this is what makes
  // the rail work everywhere.
  getQuestions?: () => any[] | Promise<any[]>;
  getPageInfo?: () => any;
  getPatientInfo?: () => any;
  getPageMetadata?: () => any;
};

/* -------------------------------------------------------------------------- */
/* Storage                                                                    */
/* -------------------------------------------------------------------------- */

function readStored<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch (_) {
    return fallback;
  }
}

function writeStored(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (_) {
    /* a full or blocked localStorage must never break the rail */
  }
}

/** Stored vertical position, or null when the rail has never been dragged. */
function readStoredTop(): number | null {
  const stored = readStored<number | null>(TOP_KEY, null);
  return typeof stored === 'number' && Number.isFinite(stored) ? stored : null;
}

function clearRetiredKeys(): void {
  RETIRED_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (_) {
      /* nothing to do */
    }
  });
}

/* -------------------------------------------------------------------------- */
/* Background bridge                                                          */
/* -------------------------------------------------------------------------- */

/** Last transport-level failure, so callers can say why nothing came back. */
let lastBridgeError: string | null = null;

/**
 * Fire-and-forget, for messages the background's main onMessage listener owns
 * (opening and closing the side panel). Those must NOT go down the compliance
 * port -- it only speaks compliance -- and we don't need their reply.
 */
function notifyBackground(message: any): void {
  try {
    if (typeof chrome === 'undefined' || !chrome.runtime?.id) return;
    chrome.runtime.sendMessage(message, () => {
      void chrome.runtime.lastError;
    });
  } catch (_) {
    /* nothing to do -- the panel just won't toggle */
  }
}

let requestId = 0;

/**
 * One request over a dedicated port. sendMessage was losing the reply: another
 * onMessage listener elsewhere in the extension can claim it, so the worker
 * computed the right answer and the rail got nothing. The codebase already hit
 * this with note-capture-upload and solved it the same way -- a port has exactly
 * one other end, so nothing else can answer for it.
 */
function sendOnce<T = any>(message: any, timeoutMs = 75000): Promise<T | null> {
  return new Promise((resolve) => {
    if (typeof chrome === 'undefined' || !chrome.runtime?.id) {
      lastBridgeError = 'the extension runtime is not available on this page';
      resolve(null);
      return;
    }

    let settled = false;
    const id = ++requestId;
    let port: chrome.runtime.Port;

    const finish = (value: T | null, error: string | null): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      lastBridgeError = error;
      try {
        port.disconnect();
      } catch (_) {
        /* already gone */
      }
      resolve(value);
    };

    const timer = setTimeout(
      () => finish(null, `the worker did not reply within ${timeoutMs / 1000}s`),
      timeoutMs
    );

    try {
      port = chrome.runtime.connect({ name: 'compliance-rail' });
    } catch (error: any) {
      settled = true;
      clearTimeout(timer);
      lastBridgeError = String(error?.message ?? error);
      resolve(null);
      return;
    }

    port.onMessage.addListener((response: any) => {
      if (response?.id !== undefined && response.id !== id) return;
      if (response?.result === undefined || response?.result === null) {
        finish(null, 'the worker replied with nothing');
        return;
      }
      finish(response.result as T, null);
    });

    port.onDisconnect.addListener(() => {
      finish(
        null,
        chrome.runtime.lastError?.message ||
          'the worker closed the connection without replying'
      );
    });

    try {
      port.postMessage({ ...message, id });
    } catch (error: any) {
      finish(null, String(error?.message ?? error));
    }
  });
}

/**
 * An MV3 service worker can be asleep, and the message that wakes it is the one
 * most likely to fail. Retry once before believing the failure.
 */
async function askBackground<T = any>(message: any): Promise<T | null> {
  const first = await sendOnce<T>(message);
  if (first !== null) return first;

  const firstError = lastBridgeError;
  await new Promise((r) => setTimeout(r, 300));
  const second = await sendOnce<T>(message);
  if (second !== null) {
    log(`bridge recovered on retry (first attempt: ${firstError})`);
    return second;
  }
  return null;
}

/** Why the last askBackground call came back empty. */
function bridgeError(): string {
  return lastBridgeError || 'no answer from the background worker';
}

/**
 * How a mounted rail reaches Comprehend. The extension's rail uses the
 * background worker; the embed's (cx_embed loader.js) uses its panel iframe.
 */
export type RailHost = {
  /** A FREE_COMPLIANCE_* request; null when nothing answered. */
  ask: <T = any>(message: any) => Promise<T | null>;
  /** Panel control, no reply: EXPAND_SIDE_PANEL and CLOSE_SIDE_PANEL. */
  notify: (message: any) => void;
  /** SIDE_PANEL_OPENED / SIDE_PANEL_CLOSED and FREE_COMPLIANCE_MIRROR_PANEL_SCORE. */
  listen: (listener: (request: any) => void) => void;
  /** Why the last ask came back empty. */
  error: () => string;
};

/**
 * The rail inside a vendor app (TPI): the same rail, but its one action is the
 * app's instead of a compliance grade. No side panel, tabs or Apps there — the
 * page is already inside Comprehend — so the rail is the handle, the mark and
 * this chip, which takes the grade chip's place, tint and tooltip card.
 */
export type RailAction = {
  /** An icon from RAIL_ACTION_ICONS. */
  icon: string;
  /** Accessible name of the chip. */
  label: string;
  /** The card tooltip: what a click does, and what it says while running. */
  title: string;
  body: string;
  hint: string;
  busyTitle: string;
  busyBody: string;
  /** Null when the action can run; otherwise why not (the card says so). */
  blocked?: () => string | null;
  run: () => Promise<void> | void;
};

const actionHost: RailHost = {
  ask: async () => null,
  notify: () => {},
  listen: () => {},
  error: () => 'no Comprehend side panel here'
};

// The action rail's re-render, for when blocked() may have changed.
const railRefresh = new WeakMap<HTMLElement, () => void>();

/**
 * Mount the rail for a vendor app's action. Returns the root (remove it to
 * unmount) and refresh(), to call when blocked() may have changed.
 */
export function mountActionRail(
  rootId: string,
  action: RailAction
): { root: HTMLElement; refresh: () => void } | null {
  if (typeof document === 'undefined' || !document.body) return null;
  const root = mountRail(() => null, actionHost, rootId, action);
  return { root, refresh: () => railRefresh.get(root)?.() };
}

const extensionHost: RailHost = {
  ask: askBackground,
  notify: notifyBackground,
  listen: (listener) => {
    chrome.runtime.onMessage.addListener((request: any) => {
      listener(request);
    });
  },
  error: bridgeError
};

/* -------------------------------------------------------------------------- */
/* Reading the note                                                           */
/*                                                                            */
/* Two shapes of injector, one interface. Prefer the capture functions where   */
/* they exist — they already return plain values — and otherwise derive the    */
/* same thing from the scrape every EMR supports.                             */
/* -------------------------------------------------------------------------- */

function canReadNote(injector: RailInjector | null): boolean {
  return Boolean(
    (injector?.getQuestionsForCapture || injector?.getQuestions) &&
      (injector?.getPageInfoForCapture || injector?.getPageInfo)
  );
}

async function readPageInfo(injector: RailInjector): Promise<any> {
  const read = injector.getPageInfoForCapture || injector.getPageInfo;
  if (!read) return null;
  return await Promise.resolve(read.call(injector));
}

async function readPatientInfo(injector: RailInjector): Promise<any> {
  const read = injector.getPatientInfoForCapture || injector.getPatientInfo;
  if (!read) return null;
  try {
    return await Promise.resolve(read.call(injector));
  } catch (_) {
    return null;
  }
}

/**
 * Keep only the values a clinician actually entered: unwrap "Existing Value"
 * and drop "Description". Mirrors extractExistingValuesOnly in
 * components/Misc/complianceUtils.js — reimplemented rather than imported
 * because that module pulls react-toastify, and this runs in the content script.
 */
function extractExistingValues(value: any): any {
  if (!value || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(extractExistingValues);
  if ('Existing Value' in value) {
    return extractExistingValues(value['Existing Value']);
  }
  const result: Record<string, any> = {};
  for (const [key, nested] of Object.entries(value)) {
    if (key === 'Description') continue;
    result[key] = extractExistingValues(nested);
  }
  return result;
}

/** Every note section on the page, keyed by question key. */
async function readNoteValues(
  injector: RailInjector
): Promise<Record<string, any>> {
  if (injector.getQuestionsForCapture) {
    const values = await Promise.resolve(injector.getQuestionsForCapture());
    return values && typeof values === 'object' ? values : {};
  }
  if (!injector.getQuestions) return {};

  const questions = await Promise.resolve(injector.getQuestions());
  const mapping: Record<string, any> = {};
  (Array.isArray(questions) ? questions : []).forEach((question: any) => {
    if (!question?.Key) return;
    const existing = question.ExistingNote || question.Structure;
    if (!existing) return;
    mapping[question.Key] = extractExistingValues(existing);
  });
  return mapping;
}

/**
 * Identifies the note on screen, so a change of patient can blank the grade.
 * Falls back to the patient + page fingerprint on EMRs with no note key — the
 * same identity react_webInjector's computeFingerprint uses.
 */
async function readNoteFingerprint(
  injector: RailInjector
): Promise<string | null> {
  if (injector.getNoteKeyForCapture) {
    const key = await Promise.resolve(injector.getNoteKeyForCapture());
    if (typeof key === 'string' && key) return key;
  }
  // Prompt's patient reader clicks the intake dialog open and Emerge's page
  // reader expands every SOAP section. Neither may run on the note poll, so on
  // those two the URL stands in for the note key.
  if (['promptemr.com', 'ehr.software'].some((host) => window.location.hostname.includes(host))) {
    return window.location.href || null;
  }
  try {
    const [patientInfo, pageInfo] = await Promise.all([
      readPatientInfo(injector),
      readPageInfo(injector)
    ]);
    const fingerprint = [
      patientInfo?.['EMR ID'] || '',
      patientInfo?.['DOB'] || '',
      pageInfo?.['URL'] || '',
      pageInfo?.['Note Type'] || ''
    ].join('|');
    return fingerprint === '|||' ? null : fingerprint;
  } catch (_) {
    return null;
  }
}

/* -------------------------------------------------------------------------- */
/* Grades                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Same thresholds as the side panel's scoreToGrade, kept here because the
 * content script must not import the React app.
 */
export function scoreToGrade(score: number | null | undefined): string {
  if (!score || score === 0) return '--';
  if (score < 50) return 'F';
  if (score < 60) return 'C';
  if (score < 80) return 'B';
  return 'A';
}

/**
 * Rail palette, muted for clinical chrome — deliberately not the brighter set
 * the modal uses. B and C share `watch`: ADR-001 forbids signalling by colour
 * alone, so the letter carries the distinction and the bar carries severity.
 */
function railGradeColor(grade: string): string {
  if (grade === 'A') return PASS;
  if (grade === 'B' || grade === 'C') return WATCH;
  if (grade === 'F') return RISK;
  return MUTED;
}

/* -------------------------------------------------------------------------- */
/* Workspace layout                                                           */
/*                                                                            */
/* The ribbon shows the same pinned items, in the same order, as the visit     */
/* view's tabs. The side panel owns the model (front end                        */
/* utils/workspace/workspaceLayout.js); these are its keys and defaults,       */
/* repeated here because the content script must not import the React app.     */
/* Both ends read and write chrome.storage.local, so a drag on either reorders  */
/* the other. The layout's home is the Comprehend account; the side panel     */
/* syncs it, and pushes the rail's edits (marked dirty) since the rail can't.  */
/* -------------------------------------------------------------------------- */

const LAYOUT_KEY = 'comprehendpt_workspace_layout';
const CATALOG_KEY = 'comprehendpt_workspace_catalog';
const ACTIVE_KEY = 'comprehendpt_workspace_active';
const LOCKED_ITEMS: string[] = [];
const DEFAULT_ORDER = ['note', 'chat', 'compliance', 'coding'];
const DEFAULT_PINNED = ['note', 'chat', 'compliance', 'coding'];
/** The rail always offers these, whatever the side panel last published. */
const RAIL_BUILTINS = ['note', 'chat', 'compliance'];

type Layout = { order: string[]; pinned: string[] };
type CatalogItem = {
  id: string;
  label: string;
  color?: string;
  /** The app's icon as raw RGBA pixels, base64 — see appGlyph. */
  iconPixels?: { size: number; data: string };
  /** Not linked to the open patient yet (a flag only; no patient details). */
  attention?: boolean;
};

const BUILTIN_LABELS: Record<string, string> = {
  note: 'Note',
  chat: 'Ask',
  compliance: 'Compliance',
  coding: 'Coding'
};

function cleanIds(value: unknown): string[] | null {
  return Array.isArray(value)
    ? Array.from(new Set(value.filter((v): v is string => typeof v === 'string' && !!v)))
    : null;
}

function normalizeLayout(stored: any): Layout {
  return {
    order: cleanIds(stored?.order) || [...DEFAULT_ORDER],
    pinned: cleanIds(stored?.pinned) || [...DEFAULT_PINNED]
  };
}

/** Same placement rules as resolveLayout in the front end. */
export function resolveRailLayout(
  stored: any,
  available: string[]
): { layout: Layout; pinned: string[]; unpinned: string[] } {
  const base = normalizeLayout(stored);
  let order = base.order;
  const pinned = new Set([...base.pinned, ...LOCKED_ITEMS]);
  available.forEach((id) => {
    if (order.includes(id)) return;
    const slot = DEFAULT_ORDER.indexOf(id);
    const later = slot === -1 ? [] : DEFAULT_ORDER.slice(slot + 1);
    const at = order.findIndex((existing) => later.includes(existing));
    order = at === -1 ? [...order, id] : [...order.slice(0, at), id, ...order.slice(at)];
    if (DEFAULT_PINNED.includes(id)) pinned.add(id);
  });
  return {
    layout: { order, pinned: order.filter((id) => pinned.has(id)) },
    pinned: order.filter((id) => pinned.has(id) && available.includes(id)),
    unpinned: order.filter((id) => !pinned.has(id) && available.includes(id))
  };
}

export function setPinnedInLayout(layout: Layout, id: string, pinned: boolean): Layout {
  if (LOCKED_ITEMS.includes(id)) return layout;
  const order = layout.order.includes(id) ? layout.order : [...layout.order, id];
  const set = new Set(layout.pinned);
  if (pinned) set.add(id);
  else set.delete(id);
  return { order, pinned: order.filter((existing) => set.has(existing)) };
}

/** Move `id` to sit before `beforeId` (null = the end). */
export function moveInLayout(layout: Layout, id: string, beforeId: string | null): Layout {
  const rest = layout.order.filter((existing) => existing !== id);
  const at = beforeId == null ? -1 : rest.indexOf(beforeId);
  const order = at === -1 ? [...rest, id] : [...rest.slice(0, at), id, ...rest.slice(at)];
  return { order, pinned: layout.pinned };
}

function storageArea(area: 'sync' | 'local'): chrome.storage.StorageArea | null {
  try {
    return typeof chrome !== 'undefined' && chrome.runtime?.id && chrome.storage?.[area]
      ? chrome.storage[area]
      : null;
  } catch (_) {
    return null;
  }
}

function readArea<T>(area: 'sync' | 'local', key: string): Promise<T | null> {
  const store = storageArea(area);
  if (!store) return Promise.resolve(null);
  return new Promise((resolve) => {
    try {
      store.get(key, (result) => resolve((result?.[key] as T) ?? null));
    } catch (_) {
      resolve(null);
    }
  });
}

/* -------------------------------------------------------------------------- */
/* Mount                                                                      */
/* -------------------------------------------------------------------------- */

export function removeComplianceRail(): void {
  document.getElementById(ROOT_ID)?.remove();
}

/**
 * Show or hide the rail the moment the setting changes (the side panel's
 * edit mode, Note Preferences, or the rail's own "Hide ribbon"), without
 * reloading the EMR page. Installed once per page, whether or not the rail
 * mounted, so turning it on works on a page that loaded with it off.
 */
function watchRailSetting(
  getInjector: () => RailInjector | null,
  lookupEmrKey: () => string | null
): void {
  if (watchingSetting) return;
  watchingSetting = true;
  try {
    chrome.storage?.onChanged?.addListener((changes, area) => {
      if (area !== 'sync' || !changes[RAIL_SETTING_KEY]) return;
      const on = changes[RAIL_SETTING_KEY].newValue;
      if (on === false) {
        removeComplianceRail();
        started = false;
        log('hidden: the Compliance Rail setting was turned off');
      } else if (on === true && !started) {
        // A beat for the worker to drop its cached "off" answer first.
        setTimeout(() => void initComplianceRail(getInjector, lookupEmrKey), 300);
      }
    });
  } catch (_) {
    /* no extension runtime: the rail only follows the setting on reload */
  }
}

/**
 * Mount the rail when this page has a note we can read and the account is
 * entitled. Safe to call on every page load: it no-ops when it shouldn't be here.
 */
export async function initComplianceRail(
  getInjector: () => RailInjector | null,
  lookupEmrKey: () => string | null
): Promise<void> {
  if (started) return;
  if (typeof document === 'undefined') return;
  if (window.location.protocol === 'chrome-extension:') return;

  clearRetiredKeys();
  watchRailSetting(getInjector, lookupEmrKey);

  const status = await askBackground<{ eligible?: boolean; reason?: string }>({
    type: 'FREE_COMPLIANCE_STATUS'
  });
  if (!status?.eligible) {
    // The worker reports which check failed -- "the flag is off" and "we never
    // managed to ask" are very different problems.
    log(`not mounting: ${status?.reason || bridgeError()}`);
    return;
  }

  const blocker = whyNotReady(getInjector, lookupEmrKey);
  if (blocker) {
    log(`not mounting: ${blocker}`);
    return;
  }

  started = true;
  if (readStored<boolean>(TRAYED_KEY, false) === true) {
    log(
      'mounted, but minimised \u2014 look for the sliver at the right edge, press ' +
        `Cmd/Ctrl+Shift+C, or run localStorage.removeItem('${TRAYED_KEY}') and reload`
    );
  } else {
    log('mounted');
  }
  mountRail(getInjector, extensionHost, ROOT_ID);
}

/**
 * The embed's rail, which replaces cx_embed loader.js's panel toggle. Unlike
 * the extension's rail there is no setting, and it mounts even when this
 * page's note can't be read: it is how the panel opens, so it must be there,
 * and the grade and chat buttons say why they can't help. The loader has
 * already checked that an injector matches this page.
 *
 * Its own root id, so a page that also runs the extension's rail doesn't have
 * one rail remove the other. Returns the root, which the loader docks to the
 * panel's edge, or null when it could not mount.
 */
export function mountEmbedComplianceRail(
  getInjector: () => RailInjector | null,
  host: RailHost
): HTMLElement | null {
  if (typeof document === 'undefined' || !document.body) return null;
  log('mounted in the embed');
  return mountRail(getInjector, host, EMBED_ROOT_ID);
}

function whyNotReady(
  getInjector: () => RailInjector | null,
  lookupEmrKey: () => string | null
): string | null {
  if (!document.body) return 'page has no body yet';
  if (!lookupEmrKey()) return 'no Comprehend EMR injector matches this URL';
  if (!canReadNote(getInjector())) {
    return 'this injector exposes no way to read the note';
  }
  return null;
}

function log(message: string): void {
  console.log(`[CPT compliance rail] ${message}`);
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  css: string
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.style.cssText = css;
  return node;
}

// Logo.svg's own viewBox (0 0 930 992.74) is taller than it is wide — scale
// the rendered height to match so the full mark isn't squashed into a square.
const LOGO_ASPECT = 992.74 / 930;

/** The full Comprehend logo (all 6 paths of Logo.svg). */
function markSvg(size: number): string {
  const height = size * LOGO_ASPECT;
  return (
    `<svg width="${size}" height="${height}" viewBox="0 0 930 992.74" ` +
    `xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style="display:block">` +
    `<path fill="currentColor" fill-rule="evenodd" d="M205,150.92c-44.62,0-81.69,39.41-81.69,86.65s37.07,84,81.69,84,79.18-36.74,79.18-84S249.51,150.92,205,150.92Z" transform="translate(-40.16 -3.63)"/>` +
    `<path fill="currentColor" fill-rule="evenodd" d="M296.07,726.61,325.72,816H130.44l17.34-81.52v-7.85ZM147.78,350.73c-24.72,0-44.49,18.36-51.94,42L41.47,653c-7.38,39.39,17.33,73.59,51.93,73.59L41.47,955.31c-4.95,15.78,4.94,28.88,17.33,34.13a21.93,21.93,0,0,0,7.77,1.42c11.3,0,20.72-9,26.83-19.77l22.21-94.71H343.07l24.71,71c8.16,28.37,30.05,44.05,51.31,44.05a42.64,42.64,0,0,0,13-2c29.66-5.25,47-39.38,42-68.33l-81.6-262.83c-4.94-26.29-27.22-42-51.93-42H283.75c-14.83,0-27.23-13.19-27.23-29,0-18.44,12.4-31.54,27.23-31.54h81.52c29.66,0,54.37-26.29,54.37-57.83,0-34.21-24.71-60.49-54.37-60.49H217l-19.77-55.15C189.75,363.83,170,350.73,147.78,350.73Z" transform="translate(-40.16 -3.63)"/>` +
    `<path fill="currentColor" fill-rule="evenodd" d="M803.59,150.92c-44.53,0-79.18,39.41-79.18,86.65s34.65,84,79.18,84,81.61-36.74,81.61-84S848.13,150.92,803.59,150.92Z" transform="translate(-40.16 -3.63)"/>` +
    `<path fill="currentColor" fill-rule="evenodd" d="M860.46,726.86v7.85l17.39,81.52H684.56l27.3-89.37Zm0-373.31c-22.25,0-39.62,13.19-49.53,31.54l-19.82,57.83H642.5c-29.72,0-54.49,26.28-54.49,57.82,0,34.21,24.77,60.49,54.49,60.49h81.78c14.86,0,27.2,13.1,27.2,28.88s-12.34,28.95-27.2,28.95h-57c-24.77,0-44.58,15.77-52.06,42.05L536,924c-7.39,31.54,7.48,63.08,39.64,70.93a52.43,52.43,0,0,0,10.92,1.16c23,0,49.23-14.18,53.47-45.8l24.78-71h228L915,973.93c4.08,13,14.86,22.44,26.89,22.44a23.44,23.44,0,0,0,7.78-1.41c14.94-5.26,24.85-21,17.39-36.81L915,732.12c34.67-5.26,59.52-39.47,54.57-76.27l-57-260.24C907.56,369.32,885.23,353.55,860.46,353.55Z" transform="translate(-40.16 -3.63)"/>` +
    `<path fill="currentColor" fill-rule="evenodd" d="M519.68,159.86a5.54,5.54,0,0,0-3.88,1.68,15.33,15.33,0,0,1-22.13,0,4.26,4.26,0,0,0-3.39-1.68,5.23,5.23,0,0,0-3.69,1.68c-2.22,1.8-2.22,4.94,0,7.18,4.42,5.38,11.51,8.07,18.14,8.07a24.41,24.41,0,0,0,18.16-8.07c2.22-1.34,2.22-4.93,0-7.18A3.61,3.61,0,0,0,519.68,159.86Z" transform="translate(-40.16 -3.63)"/>` +
    `<path fill="currentColor" fill-rule="evenodd" d="M615.53,3.63H393c-47,0-81.66,39.42-81.66,89.45v60.55c0,47.36,37.14,84.2,81.66,84.2h98.92L593.32,345.74a32.59,32.59,0,0,0,20.49,6.76,47.27,47.27,0,0,0,11.62-1.5c9.89-5.26,14.84-18.46,14.84-26.4v-92c32.18-13.19,56.92-44.76,56.92-84.19V93.08C697.19,43.05,657.62,3.63,615.53,3.63ZM504.38,28.75c24.14,0,44.77,12.89,57.06,32h-25.9c-12.73,0-24.14-5.33-33.37-14.23-4.38-4.88-8.33-10.21-10.53-16.43C496,29.64,500,28.75,504.38,28.75ZM425.71,98.84V132.5c-6-4-10.66-10.63-10.66-18.17,0-8,4.63-15,10.66-17.71Zm10.67-1.3a68.88,68.88,0,0,1,45.54-64.9A54.05,54.05,0,0,0,495.18,54a56.34,56.34,0,0,0,40.68,16.88h30.51a61.67,61.67,0,0,1,5.74,26.68v4.89H561.05a25.76,25.76,0,0,0-25.63-20.9,26,26,0,0,0-25.21,20.9H498.28a25.32,25.32,0,0,0-25.21-20.9,25.76,25.76,0,0,0-25.63,20.9H436.38Zm114.39,10.72a15.4,15.4,0,0,1-15.49,15.5,15.5,15.5,0,1,1,0-31A15.4,15.4,0,0,1,550.77,108.26Zm-62,0a15.51,15.51,0,1,1-15.51-15.5A15.69,15.69,0,0,1,488.73,108.26Zm16,100.83c-31.87,0-59.32-22.88-66.4-52.93h35c.88,0,1.76-.89,2.65-1.34a51,51,0,0,1,57.12,0c.44.45,1.77.45,3.09,1.34h35C564.06,186.21,537.06,209.09,504.73,209.09Zm68.35-69.84v4.88H538a60.19,60.19,0,0,0-32.71-9.65A63.51,63.51,0,0,0,472,144.13H437.36v-31h11.08a25.33,25.33,0,0,0,25.28,21.27,26.13,26.13,0,0,0,25.73-21.27H511a25.93,25.93,0,0,0,51,0h11.09Zm9.7-7.74V95.65a20,20,0,0,1,10.66,17.5C593.44,121,589,127.58,582.78,131.51Z" transform="translate(-40.16 -3.63)"/>` +
    `</svg>`
  );
}

/** Points left to open the panel, right to close it again. */
function chevronSvg(direction: 'left' | 'right'): string {
  const path =
    direction === 'left' ? 'M7.6 1.8 3.4 6l4.2 4.2' : 'M4.4 1.8 8.6 6l-4.2 4.2';
  return (
    `<svg viewBox="0 0 12 12" fill="none" width="13" height="13" aria-hidden="true">` +
    `<path d="${path}" stroke="currentColor" stroke-width="1.7" ` +
    `stroke-linecap="round" stroke-linejoin="round"/></svg>`
  );
}

/** Which tab the side panel should open a visit on: any ribbon item, or 'apps'. */
type LandingTab = string;

/**
 * The built-in apps' icons. An exact copy of WORKSPACE_ICONS in the front end
 * (utils/workspace/workspaceIcons.js), so the rail and the tabs wear the same
 * set; workspaceIcons.test.js there fails if this copy drifts.
 */
const WORKSPACE_ICONS: Record<string, string> = {
  note:
    '<path d="M7 3h7l5 5v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z"/>' +
    '<path d="M14 3v5h5"/><path d="M8.5 13h7M8.5 16.5h4.5"/>',
  // Ask (COM-326): a chat bubble with sparkles.
  chat:
    '<path d="M13 4H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h1v3l4-3h5a2 2 0 0 0 2-2v-3"/>' +
    '<path d="M8 9h4M8 12h6"/>' +
    '<path d="M18.5 2.5l.9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9z"/>' +
    '<path d="M20.5 15.5l.5 1.1 1.1.5-1.1.5-.5 1.1-.5-1.1-1.1-.5 1.1-.5z"/>',
  // A shield with a check: protected, documentation that holds up.
  compliance:
    '<path d="M12 3 4.5 6v5.6c0 4.5 3.1 8.1 7.5 9.4 4.4-1.3 7.5-4.9 7.5-9.4V6Z"/>' +
    '<path d="m8.8 12.2 2.3 2.3 4.2-4.6"/>',
  summary:
    '<path d="M4 7h8M4 12h10M4 17h7"/>' +
    '<path d="M18 3.5 19 6l2.5 1-2.5 1-1 2.5-1-2.5-2.5-1 2.5-1Z"/>',
  // Billing codes: $$.
  coding:
    '<path d="M10 8.4c-.4-1.3-1.6-2.1-3-2.1-1.8 0-3 1-3 2.4 0 3.2 6 1.9 6 5.3 0 1.5-1.3 2.5-3.1 2.5-1.5 0-2.7-.8-3.1-2.1M7 4.3v2M7 16.3v2.2M20 8.4c-.4-1.3-1.6-2.1-3-2.1-1.8 0-3 1-3 2.4 0 3.2 6 1.9 6 5.3 0 1.5-1.3 2.5-3.1 2.5-1.5 0-2.7-.8-3.1-2.1M17 4.3v2M17 16.3v2.2"/>',
  apps:
    '<rect x="3.5" y="3.5" width="7" height="7" rx="2"/><rect x="13.5" y="3.5" width="7" height="7" rx="2"/>' +
    '<rect x="3.5" y="13.5" width="7" height="7" rx="2"/><rect x="13.5" y="13.5" width="7" height="7" rx="2"/>'
};

/** 19px keeps the 1.75/24 strokes at ~1.4px, the weight the rail's glyphs always had. */
const RIBBON_ICON = 19;

/**
 * Icons for a vendor app's action chip (RailAction.icon). Not workspace items,
 * so not in WORKSPACE_ICONS, which must match the front end's copy.
 */
const RAIL_ACTION_ICONS: Record<string, string> = {
  // Comprehend this screen: a magic wand writing, with sparkles — the note
  // generation, done here.
  comprehend:
    '<path d="m4 20 10.5-10.5"/><path d="m12.5 7.5 4 4"/>' +
    '<path d="M18 2.5l.9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9z"/>' +
    '<path d="M20.5 12.5l.5 1.1 1.1.5-1.1.5-.5 1.1-.5-1.1-1.1-.5 1.1-.5z"/>' +
    '<path d="M9.5 2.5l.5 1.1 1.1.5-1.1.5-.5 1.1-.5-1.1-1.1-.5 1.1-.5z"/>'
};

function iconSvg(id: string, size = 16): string {
  return (
    `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" ` +
    `stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" ` +
    `style="display:block">${WORKSPACE_ICONS[id] || RAIL_ACTION_ICONS[id] || ''}</svg>`
  );
}

function mountRail(
  getInjector: () => RailInjector | null,
  host: RailHost,
  rootId: string,
  // A vendor app's rail (mountActionRail): this chip instead of the grade, and
  // none of the panel controls.
  action: RailAction | null = null
): HTMLElement {
  document.getElementById(rootId)?.remove();
  ensureComplianceSpinnerStyle();

  /* ------------------------------- state ------------------------------- */

  let grade: string | null = null;
  let reasons: ComplianceReason[] = [];
  /** The note the current grade belongs to; a different note blanks it. */
  let gradedNoteKey: string | null = null;
  let conversationId: number | null = null;
  /**
   * The note the worker confirmed conversationId for (this patient's, not
   * deleted). A visit adopted from the side panel isn't confirmed: the panel
   * can be on any patient, so a ribbon button re-resolves before opening it.
   */
  let visitNoteKey: string | null = null;
  let scoring = false;
  // A ribbon button is asking the worker for a visit to open.
  let resolvingVisit = false;
  let trayed = readStored<boolean>(TRAYED_KEY, false) === true;
  // Vertical position in px. null = not placed yet, so it centres itself.
  let top: number | null = readStoredTop();
  // Declared up here, not next to the peek handlers: applyTrayed() runs during
  // mount and calls hidePeek(), which would hit this in its TDZ and throw for
  // anyone whose rail was trayed.
  let peekTimer: any = null;
  // Whether the Comprehend side panel is open for this tab. The background
  // worker already broadcasts SIDE_PANEL_OPENED / SIDE_PANEL_CLOSED to the tab
  // when it opens or closes one, so the chevron can be a real toggle.
  let panelOpen = false;

  /* ------------------------------ elements ----------------------------- */

  const root = el(
    'div',
    `position:fixed;right:0;z-index:${Z};font-family:${SANS};box-sizing:border-box;`
  );
  root.id = rootId;

  const rail = el(
    'div',
    `display:flex;flex-direction:column;align-items:center;width:${RAIL_WIDTH}px;` +
      `background:${SURFACE};box-shadow:${SHADOW};border-radius:${EDGE_RADIUS}px 0 0 ${EDGE_RADIUS}px;` +
      'padding:9px 0 5px;gap:7px;box-sizing:border-box;position:relative;'
  );

  // Drag handle: the embed's dot grip (loader.js) -- 4px rounded squares in
  // brand colour, touch-action none so a touch drag doesn't scroll the EMR.
  // Turned 90 degrees from the embed's 2x3: three columns of two, so the handle
  // is wider than it is tall, which is how a move-up-and-down grip reads.
  const grip = el(
    'div',
    'display:flex;flex-direction:row;align-items:center;justify-content:center;' +
      `gap:2px;cursor:grab;touch-action:none;color:${BRAND};flex:none;` +
      'padding:1px 0;'
  );
  grip.setAttribute('aria-label', 'Move the Comprehend rail up or down');
  grip.title = 'Drag to move up or down';
  for (let column = 0; column < 3; column++) {
    const col = el(
      'div',
      'display:flex;flex-direction:column;align-items:center;gap:2px;'
    );
    for (let row = 0; row < 2; row++) {
      col.appendChild(
        el(
          'span',
          'display:block;width:4px;height:4px;border-radius:2px;' +
            'background:currentColor;'
        )
      );
    }
    grip.appendChild(col);
  }

  // Minimise to the tray. Hidden until hover: ADR-001 wants the idle rail to be
  // the mark alone, and permanent chrome shouldn't advertise its own exit.
  const minimize = el(
    'button',
    `position:absolute;top:1px;left:3px;width:13px;height:13px;border:0;padding:0;` +
      `background:transparent;color:${MUTED};font-family:${SANS};font-size:12px;` +
      'line-height:13px;cursor:pointer;opacity:0;transition:opacity .12s;'
  );
  minimize.type = 'button';
  minimize.textContent = '\u00d7';
  minimize.title = 'Minimise — click the sliver or Cmd/Ctrl+Shift+C to bring it back';
  minimize.setAttribute('aria-label', 'Minimise the Comprehend compliance rail');

  // The mark is the score button. It's the only always-present control, and
  // "check this note" is the rail's one action.
  const mark = el(
    'button',
    `border:0;background:transparent;padding:0;cursor:pointer;color:${BRAND};` +
      'flex:none;display:flex;align-items:center;justify-content:center;'
  );
  mark.type = 'button';
  const logo = document.createElement('img');
  logo.src = MARK_PNG;
  logo.alt = 'Comprehend';
  logo.style.cssText =
    `width:${MARK_SIZE}px;height:${MARK_SIZE}px;object-fit:contain;display:block;` +
    'pointer-events:none;';
  // Some EMRs ship a CSP that blocks data: images. Fall back to the inline
  // vector mark rather than leaving a hole where the identity should be.
  logo.addEventListener('error', () => {
    // Same visual size as the PNG, whose logo fills only the middle ~64% of
    // its square (16px of padding each side at 90px): drawn edge to edge at
    // MARK_SIZE the vector reads half again as large.
    mark.innerHTML =
      `<span style="width:${MARK_SIZE}px;height:${MARK_SIZE}px;display:flex;` +
      `align-items:center;justify-content:center">${markSvg(Math.round((MARK_SIZE * 0.64) / LOGO_ASPECT))}</span>`;
  });
  mark.appendChild(logo);

  // Scoring indicator, in the slot the grade will occupy. ADR-001 bans motion
  // that solicits attention -- this is the opposite: direct feedback for a click
  // the clinician just made, on a call that can take tens of seconds. The
  // mockup's 6px static dot was too easy to miss for that long a wait.
  const spinner = el(
    'div',
    'width:16px;height:16px;border-radius:50%;flex:none;margin:1px 0;' +
      `border:2px solid ${LINE};border-top-color:${MUTED};` +
      'animation:comp-spin 0.8s linear infinite;'
  );
  spinner.setAttribute('role', 'progressbar');
  spinner.setAttribute('aria-label', action ? action.busyTitle : 'Checking compliance');

  const gradeBtn = el(
    'button',
    'border:0;background:transparent;padding:0;cursor:pointer;flex:none;' +
      `display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2px;` +
      `min-width:${BUTTON_SIZE}px;min-height:${BUTTON_SIZE}px;border-radius:${BUTTON_RADIUS}px;` +
      'transition:background .1s;'
  );
  gradeBtn.type = 'button';
  // Tinted, always: the one control that acts rather than opens. Its fill
  // follows the grade (renderGrade → paintChip).
  const chipTint = () => CHIP_TINT[grade && grade !== '--' ? grade : 'none'] || CHIP_TINT.none;
  function paintChip(hover = false): void {
    const tint = chipTint();
    gradeBtn.style.background = hover ? tint.hover : tint.bg;
    gradeBtn.style.boxShadow = `inset 0 0 0 1px ${tint.line}`;
  }
  gradeBtn.addEventListener('mouseenter', () => paintChip(true));
  gradeBtn.addEventListener('mouseleave', () => paintChip(false));
  // Before a grade exists the slot shows the compliance mark; after one, the
  // letter. The raw number out of 100 is noise at this size -- the grade is the
  // signal, and the modal has the detail.
  // Same ink as the chevron and chat glyphs it sits between, so the three read
  // as one set rather than the compliance mark looking disabled.
  const gradeIcon = el('div', `display:none;color:${PASS};`);
  gradeIcon.innerHTML = iconSvg(action ? action.icon : 'compliance', RIBBON_ICON);
  if (action) gradeBtn.setAttribute('aria-label', action.label);
  const gradeLetter = el(
    'b',
    `font-family:${MONO};font-weight:600;font-size:18px;line-height:1;`
  );
  gradeBtn.appendChild(gradeIcon);
  gradeBtn.appendChild(gradeLetter);

  // The round icon buttons under the mark: the panel chevron and the ribbon's.
  // `tip` is the short name the hover tooltip shows; `label` the fuller
  // accessible name. No native title: the rail draws its own tooltip.
  function iconButton(label: string, tip = label): HTMLButtonElement {
    const button = el(
      'button',
      `width:${BUTTON_SIZE}px;height:${BUTTON_SIZE}px;border-radius:${BUTTON_RADIUS}px;` +
        'border:0;background:transparent;cursor:pointer;display:flex;align-items:center;' +
        `justify-content:center;color:${CHEV_INK};flex:none;padding:0;` +
        'transition:background .1s,color .1s;'
    );
    button.type = 'button';
    button.dataset.tip = tip;
    button.setAttribute('aria-label', label);
    button.addEventListener('mouseenter', () => {
      button.style.background = BUTTON_HOVER;
      button.style.color = INK;
    });
    button.addEventListener('mouseleave', () => {
      button.style.background = 'transparent';
      button.style.color = button.dataset.active === '1' ? ACTIVE_INK : CHEV_INK;
    });
    return button;
  }

  const chevron = iconButton('Open Comprehend');

  // The ribbon: a button per pinned item, in the clinician's order (the same
  // order as the visit view's tabs), then Apps. Compliance takes its place in
  // that order as the grade slot (the spinner and the grade travel together);
  // its tinted surround is what says it can be clicked for a score.
  const ribbon = el(
    'div',
    'display:flex;flex-direction:column;align-items:center;gap:7px;position:relative;'
  );
  const complianceSlot = el(
    'div',
    'display:flex;flex-direction:column;align-items:center;touch-action:none;'
  );
  complianceSlot.appendChild(spinner);
  complianceSlot.appendChild(gradeBtn);

  const appsButton = iconButton('All apps for this visit', 'Apps');
  appsButton.innerHTML = iconSvg('apps', RIBBON_ICON);

  // Order: handle, identity, the panel control right under it, the ribbon,
  // then Apps.
  rail.appendChild(minimize);
  rail.appendChild(grip);
  rail.appendChild(mark);
  if (!action) rail.appendChild(chevron);
  rail.appendChild(ribbon);
  if (!action) rail.appendChild(appsButton);

  // Tray: what's left when minimised. Never unmounts, so the rail can always
  // come back without devtools.
  // 7px at rest, as ADR-001 asks. But it is the only way back from the tray, so
  // it can't be a 32%-opacity grey hairline nobody can find: brand colour, and
  // it grows under the cursor so brushing the edge reveals it.
  const tray = el(
    'button',
    `width:${TRAY_WIDTH}px;height:${TRAY_HEIGHT}px;border:0;padding:0;` +
      `border-radius:4px 0 0 4px;background:${BRAND};opacity:.75;cursor:pointer;` +
      `box-shadow:-1px 0 4px rgba(14,17,22,.18);display:none;` +
      'transition:width .12s,opacity .12s;'
  );
  tray.type = 'button';
  tray.title = action ? 'Show Comprehend' : 'Show Comprehend compliance';
  tray.setAttribute('aria-label', action ? 'Show the Comprehend rail' : 'Show Comprehend compliance rail');
  if (action) minimize.setAttribute('aria-label', 'Minimise the Comprehend rail');
  tray.addEventListener('mouseenter', () => {
    tray.style.width = `${TRAY_WIDTH + 5}px`;
    tray.style.opacity = '1';
  });
  tray.addEventListener('mouseleave', () => {
    tray.style.width = `${TRAY_WIDTH}px`;
    tray.style.opacity = '.75';
  });

  // The hover tooltip: one element, moved beside whichever button is under
  // the pointer. A dark pill naming the item for most; a card for Compliance.
  const PEEK_BASE =
    `position:absolute;right:${RAIL_WIDTH + 6}px;transform:translateY(-50%);` +
    'pointer-events:none;transition:opacity .12s;box-sizing:border-box;';
  const peek = el('div', `${PEEK_BASE}top:0;opacity:0;`);
  peek.setAttribute('role', 'tooltip');
  let peekFor: HTMLElement | null = null;

  root.appendChild(peek);
  root.appendChild(rail);
  root.appendChild(tray);
  document.body.appendChild(root);

  /* ------------------------------ rendering ---------------------------- */

  function clampTop(value: number): number {
    const height = root.getBoundingClientRect().height || 88;
    const max = Math.max(EDGE_MARGIN, window.innerHeight - height - EDGE_MARGIN);
    return Math.min(Math.max(EDGE_MARGIN, value), max);
  }

  function applyPlacement(): void {
    const height = root.getBoundingClientRect().height || 88;
    // Unplaced rails sit centred; once dragged, the clinician's own position
    // wins and is only clamped back inside the window.
    let wanted = top === null ? (window.innerHeight - height) / 2 : top;
    // On a page with its own right-edge tab (Stride's chat), an unplaced rail
    // starts above that tab instead of centred on top of it: above the real
    // tab when it is on the page, above where it will be when it is not yet.
    if (top === null && needsSidebarPlacement(window.location.hostname)) {
      const tabTop =
        sidebarTabTop(document) ?? (window.innerHeight - SIDEBAR_TAB_ESTIMATED_HEIGHT) / 2;
      wanted = Math.min(wanted, tabTop - SIDEBAR_TAB_GAP - height);
    }
    root.style.top = `${clampTop(wanted)}px`;
  }

  function applyTrayed(): void {
    rail.style.display = trayed ? 'none' : 'flex';
    tray.style.display = trayed ? 'block' : 'none';
    if (trayed) hidePeek();
    applyPlacement();
  }

  function setTrayed(next: boolean): void {
    trayed = next;
    writeStored(TRAYED_KEY, next);
    applyTrayed();
  }

  function renderGrade(): void {
    if (scoring) {
      spinner.style.display = 'block';
      gradeBtn.style.display = 'none';
      mark.style.opacity = '.5';
      mark.style.cursor = 'default';
      applyPlacement();
      return;
    }

    spinner.style.display = 'none';
    mark.style.opacity = '1';
    mark.style.cursor = 'pointer';

    if (!grade || grade === '--') {
      // Unscored, but the slot still shows: the compliance mark in the letter's
      // place tells the clinician a grade belongs here and that clicking gets
      // one. An empty rail gives them nothing to aim at.
      gradeBtn.style.display = 'flex';
      gradeIcon.style.display = 'block';
      gradeLetter.style.display = 'none';
      paintChip();
      if (action) {
        // Still there when it can't run, so the card can say why.
        const blocked = Boolean(action.blocked?.());
        gradeBtn.style.opacity = blocked ? '.45' : '1';
        gradeBtn.style.cursor = blocked ? 'default' : 'pointer';
        gradeBtn.setAttribute('aria-disabled', blocked ? 'true' : 'false');
      }
      applyPlacement();
      return;
    }

    gradeBtn.style.display = 'flex';
    gradeIcon.style.display = 'none';
    gradeLetter.style.display = 'block';
    gradeLetter.textContent = grade;
    gradeLetter.style.color = railGradeColor(grade);
    paintChip();
    applyPlacement();
  }

  /** Swap the tooltip's look, keeping where it is and whether it shows. */
  function setPeekLook(look: string): void {
    const { top, opacity } = peek.style;
    peek.style.cssText = `${PEEK_BASE}${look}top:${top || '0'};opacity:${opacity || '0'};`;
  }

  function renderPeek(): void {
    const target = peekFor;
    if (!target) return;
    if (target !== complianceSlot && !complianceSlot.contains(target)) {
      setPeekLook(
        `background:${INK};color:#fff;border:0;border-radius:6px;padding:5px 9px;` +
        `box-shadow:0 2px 8px rgba(14,17,22,.25);width:auto;white-space:nowrap;`
      );
      peek.textContent = target.dataset.tip || target.getAttribute('aria-label') || '';
      return;
    }
    // Compliance: say what the click does, in a card that looks different
    // from every other tooltip, because it is the one that acts rather than opens.
    setPeekLook(
      `background:${TIP_COMPLIANCE_BG};color:${INK};border:1px solid ${TIP_COMPLIANCE_LINE};` +
      `border-radius:8px;padding:9px 12px;box-shadow:${SHADOW};width:${PEEK_WIDTH}px;white-space:normal;`
    );
    const title = (text: string, color = PASS): string =>
      `<div style="font:700 12.5px/1.2 ${SANS};color:${color};margin-bottom:3px">${text}</div>`;
    const body = (text: string): string =>
      `<div style="font:12px/1.4 ${SANS};color:${INK2}">${text}</div>`;
    const hint = (text: string): string =>
      `<div style="font:600 11px/1.2 ${SANS};color:${PASS};margin-top:7px">${text}</div>`;
    if (action) {
      const blocked = action.blocked?.() || null;
      // Vendor-supplied wording is ours (the adapter's), but set as text anyway.
      peek.innerHTML = title('') + body('') + (scoring || blocked ? '' : hint(''));
      const [t, b, h] = Array.from(peek.children) as HTMLElement[];
      t.textContent = scoring ? action.busyTitle : action.title;
      b.textContent = scoring ? action.busyBody : blocked || action.body;
      if (h) h.textContent = action.hint;
      return;
    }
    if (scoring) {
      peek.innerHTML = title('Checking compliance\u2026') + body('Grading this note against your documentation criteria.');
      return;
    }
    if (!grade || grade === '--') {
      peek.innerHTML =
        title('Check compliance') +
        body('Grade this note against your documentation criteria before you sign.') +
        hint('Click to grade \u2192');
      return;
    }
    const gaps = complianceGapCount(reasons);
    peek.innerHTML =
      title(`Grade ${grade}`, railGradeColor(grade)) +
      body(
        gaps > 0
          ? `${gaps} item${gaps === 1 ? '' : 's'} to address before this note is clean.`
          : 'This note meets the documentation criteria.'
      ) +
      hint('Click for the review \u2192');
  }

  function renderChevron(): void {
    chevron.innerHTML = chevronSvg(panelOpen ? 'right' : 'left');
    const label = panelOpen ? 'Close Comprehend' : 'Open Comprehend';
    chevron.dataset.tip = label;
    chevron.setAttribute('aria-label', `${label} side panel`);
    chevron.setAttribute('aria-expanded', panelOpen ? 'true' : 'false');
    // The open tab's mark only means something while the panel is open.
    markActive();
  }

  /* ------------------------------- ribbon ------------------------------ */

  // The stored layout (chrome.storage.sync) and what the side panel last said
  // this account can open (chrome.storage.local). Both empty = the defaults.
  let layout: any = null;
  // Whose layout the cache holds (the side panel writes it); edits keep it.
  let layoutOwner: string | null = null;
  let catalog: CatalogItem[] = [];
  const itemButtons = new Map<string, HTMLElement>();
  // A drag just ended on a button: swallow the click it would otherwise fire.
  let suppressClick = false;
  let reordering = false;

  const catalogEntry = (id: string): CatalogItem | undefined =>
    catalog.find((item) => item.id === id);
  const labelFor = (id: string): string =>
    catalogEntry(id)?.label || BUILTIN_LABELS[id] || id;
  const availableItems = (): string[] =>
    Array.from(new Set([...RAIL_BUILTINS, ...catalog.map((item) => item.id)]));

  /**
   * A connected app's own icon. The side panel fetches and rasterizes it
   * (publishCatalog in workspaceLayout.js) and hands over raw pixels, which
   * are painted onto a canvas: no request leaves the customer's EMR page, and
   * no img-src CSP can block pixels. Apps that registered no icon get their
   * brand-coloured initial.
   */
  function appGlyph(item: CatalogItem | undefined): HTMLElement {
    const pixels = item?.iconPixels;
    if (pixels && pixels.size > 0 && typeof pixels.data === 'string') {
      try {
        const bytes = Uint8ClampedArray.from(atob(pixels.data), (c) => c.charCodeAt(0));
        if (bytes.length === pixels.size * pixels.size * 4) {
          const canvas = document.createElement('canvas');
          canvas.width = pixels.size;
          canvas.height = pixels.size;
          canvas.style.cssText =
            'width:20px;height:20px;border-radius:6px;display:block;pointer-events:none;';
          canvas.getContext('2d')?.putImageData(new ImageData(bytes, pixels.size, pixels.size), 0, 0);
          return canvas;
        }
      } catch (_) {
        /* fall through to the initial */
      }
    }
    const box = el(
      'span',
      'width:20px;height:20px;border-radius:6px;display:flex;align-items:center;' +
        `justify-content:center;background:${item?.color || BRAND};color:#fff;` +
        `font:700 11px/1 ${SANS};pointer-events:none;`
    );
    box.textContent = (item?.label || '?').slice(0, 1).toUpperCase();
    return box;
  }

  function buttonFor(id: string): HTMLElement {
    const existing = itemButtons.get(id);
    if (existing) return existing;
    let node: HTMLElement;
    if (id === 'compliance') {
      node = complianceSlot;
    } else {
      const label =
        id === 'note'
          ? "Open this visit's note in Comprehend"
          : id === 'chat'
            ? 'Ask about this visit in Comprehend'
            : `Open ${labelFor(id)} in Comprehend`;
      const button = iconButton(label, labelFor(id));
      button.style.touchAction = 'none';
      withTip(button);
      if (WORKSPACE_ICONS[id]) button.innerHTML = iconSvg(id, RIBBON_ICON);
      else button.appendChild(appGlyph(catalogEntry(id)));
      // The same dot the app's tab wears: waiting on the clinician to link
      // this patient. Its tooltip says so; the click opens the app.
      const entry = catalogEntry(id);
      if (entry?.attention) {
        button.style.position = 'relative';
        const dot = el(
          'span',
          'position:absolute;top:1px;right:1px;width:9px;height:9px;border-radius:50%;' +
            `background:${entry.color || BRAND};box-shadow:0 0 0 2px ${SURFACE};pointer-events:none;`
        );
        dot.dataset.railAttention = '1';
        button.appendChild(dot);
        button.dataset.tip = `${labelFor(id)} \u00b7 not linked to this patient yet`;
      }
      button.addEventListener('click', (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        hidePeek();
        void openVisitTab(id);
      });
      node = button;
    }
    node.dataset.railItem = id;
    enableReorder(node, id);
    itemButtons.set(id, node);
    return node;
  }

  /* ----------------------------- edit layout ---------------------------- */

  // Right-click any ribbon button (or Apps) for its menu; Edit layout makes
  // the buttons jiggle and drag, with everything unpinned shown under a
  // divider so it can be dragged up to pin, or a − / + badge clicked. Same
  // model as the side panel's edit mode. Nothing drags outside edit mode.
  let editing = false;

  const divider = el(
    'div',
    `width:24px;height:1px;background:${LINE};margin:2px 0;flex:none;`
  );
  divider.dataset.railDivider = '1';

  const doneButton = iconButton('Done rearranging', 'Done');
  doneButton.innerHTML =
    '<svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" ' +
    'stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" ' +
    'style="display:block"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>';
  doneButton.style.color = BRAND;
  doneButton.style.display = 'none';
  rail.appendChild(doneButton);

  function ensureEditStyle(): void {
    if (document.getElementById(`${ROOT_ID}-edit-style`)) return;
    const style = document.createElement('style');
    style.id = `${ROOT_ID}-edit-style`;
    style.textContent =
      `@keyframes cpt-rail-jiggle{0%{transform:rotate(-2.5deg)}50%{transform:rotate(2.5deg)}100%{transform:rotate(-2.5deg)}}` +
      `#${ROOT_ID} .cpt-rail-jiggle{animation:cpt-rail-jiggle .28s ease-in-out infinite;cursor:grab}` +
      `#${ROOT_ID} .cpt-rail-jiggle.cpt-rail-jiggle-b{animation-delay:-.14s}` +
      `@media (prefers-reduced-motion: reduce){#${ROOT_ID} .cpt-rail-jiggle{animation:none;outline:1px dashed ${MUTED};outline-offset:1px}}`;
    (document.head || document.documentElement).appendChild(style);
  }

  function badge(kind: 'remove' | 'add', label: string, onClick: () => void): HTMLElement {
    const node = el(
      'span',
      'position:absolute;top:-3px;left:-3px;width:14px;height:14px;border-radius:50%;' +
        `background:${kind === 'remove' ? MUTED : BRAND};color:#fff;font:700 12px/13px ${SANS};` +
        'text-align:center;box-shadow:0 1px 2px rgba(0,0,0,.25);cursor:pointer;z-index:2;'
    );
    node.textContent = kind === 'remove' ? '−' : '+';
    node.title = label;
    node.setAttribute('role', 'button');
    node.setAttribute('aria-label', label);
    node.dataset.railBadge = '1';
    node.addEventListener('pointerdown', (e) => e.stopPropagation());
    node.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    });
    return node;
  }

  function renderRibbon(): void {
    if (action) {
      // The action chip is the whole ribbon.
      ribbon.replaceChildren(complianceSlot);
      applyPlacement();
      return;
    }
    if (reordering) return;
    // Compliance (the grade chip) is one of the items, in the clinician's
    // order like the rest; unpinned, it leaves the rail with them.
    const resolved = resolveRailLayout(layout, availableItems());
    const { pinned, unpinned } = resolved;
    const nodes: HTMLElement[] = pinned.map(buttonFor);
    if (editing) {
      ensureEditStyle();
      nodes.push(divider, ...unpinned.map(buttonFor));
    }
    nodes.forEach((node, index) => {
      node.querySelectorAll('[data-rail-badge]').forEach((b) => b.remove());
      if (node === divider) return;
      const id = node.dataset.railItem as string;
      const isPinned = pinned.includes(id);
      node.classList.toggle('cpt-rail-jiggle', editing);
      node.classList.toggle('cpt-rail-jiggle-b', editing && index % 2 === 1);
      node.style.position = 'relative';
      node.style.opacity = editing && !isPinned ? '.6' : '';
      if (editing && !LOCKED_ITEMS.includes(id)) {
        node.appendChild(
          isPinned
            ? badge('remove', `Move ${labelFor(id)} into Apps`, () => place(id, false, null))
            : badge('add', `Pin ${labelFor(id)}`, () => place(id, true, null))
        );
      }
    });
    ribbon.replaceChildren(...nodes);
    appsButton.style.display = editing ? 'none' : 'flex';
    doneButton.style.display = editing ? 'flex' : 'none';
    markActive();
    applyPlacement();
  }

  function saveLayout(): void {
    if (!storageArea('local')) {
      // The embed: the panel saves it (and sends it to the account).
      void host.ask({ type: 'WORKSPACE_LAYOUT_SET', layout: { order: layout?.order, pinned: layout?.pinned } });
      return;
    }
    try {
      // Keep whose layout this is; dirty = the side panel still has to send
      // it to the account.
      storageArea('local')?.set({
        [LAYOUT_KEY]: { order: layout?.order, pinned: layout?.pinned, ...(layoutOwner ? { owner: layoutOwner } : {}), dirty: true }
      });
    } catch (_) {
      /* the new order still holds on this page */
    }
  }

  /**
   * Pin or unpin `id` and put it before `beforeVisible` (null = the end of
   * that group) — every move in edit mode, dragged or clicked.
   */
  function place(id: string, pinned: boolean, beforeVisible: string | null): void {
    const keepPinned = LOCKED_ITEMS.includes(id) ? true : pinned;
    const resolved = resolveRailLayout(layout, availableItems());
    let next = setPinnedInLayout(resolved.layout, id, keepPinned);
    let beforeId = beforeVisible;
    if (beforeId === null) {
      // The end of the group: right after its last button, not after
      // everything hidden, so it lands in the same spot among the tabs.
      const group = (keepPinned ? resolved.pinned : resolved.unpinned).filter((x) => x !== id);
      const last = group[group.length - 1];
      const rest = next.order.filter((x) => x !== id);
      beforeId = last ? (rest[rest.indexOf(last) + 1] ?? null) : null;
    }
    layout = moveInLayout(next, id, beforeId);
    renderRibbon();
    saveLayout();
  }

  /* ------------------------- what the panel shows ----------------------- */

  // The side panel publishes the item it has on screen (ACTIVE_KEY); while
  // the panel is open, that item's button is marked — the link between the
  // rail and the tabs, made visible.
  let activeId: string | null = null;

  function markActive(): void {
    const on = panelOpen && !editing ? activeId : null;
    const ring = (button: HTMLElement, active: boolean, ink = true): void => {
      button.dataset.active = active ? '1' : '';
      button.style.outline = active ? `2px solid ${ACTIVE_RING}` : '';
      button.style.outlineOffset = active ? '1px' : '';
      if (ink) button.style.color = active ? ACTIVE_INK : CHEV_INK;
    };
    itemButtons.forEach((button, id) => {
      if (id === 'compliance') return;
      ring(button, id === on);
    });
    ring(appsButton, on === 'apps');
    // The grade chip keeps its own tint and ink; it just wears the same ring.
    ring(gradeBtn, on === 'compliance', false);
  }

  function setEditing(on: boolean): void {
    editing = on;
    hideMenu();
    hidePeek();
    renderRibbon();
  }

  // Drag a button up or down the ribbon, and across the divider to pin or
  // unpin — edit mode only. Move/up are followed on the window rather than by
  // pointer capture: a quick flick leaves the button before the first move
  // arrives, and capture would re-target the grade slot's click to its wrapper.
  function enableReorder(node: HTMLElement, id: string): void {
    node.addEventListener('pointerdown', (down: PointerEvent) => {
      if (!editing || down.button !== 0) return;
      down.preventDefault();
      const pointerId = down.pointerId;
      const startY = down.clientY;
      // Where on the button it was grabbed, so it stays under the pointer as
      // it changes places.
      const grabY = down.clientY - node.getBoundingClientRect().top;
      let moved = false;

      // Live re-flow, home-screen style: the button moves to its new place in
      // the list as it is dragged, so its neighbours slide out of the way
      // (across the divider too), and it keeps following the pointer.
      const follow = (clientY: number): void => {
        const others = Array.from(ribbon.children).filter(
          (child) => child !== node
        ) as HTMLElement[];
        const below = others.find((child) => {
          const rect = child.getBoundingClientRect();
          return clientY < rect.top + rect.height / 2;
        });
        const want = below || null;
        if (node.nextSibling !== want) ribbon.insertBefore(node, want);
        node.style.transform = '';
        const natural = node.getBoundingClientRect().top;
        node.style.transform = `translateY(${clientY - grabY - natural}px)`;
      };

      const onMove = (e: PointerEvent): void => {
        if (e.pointerId !== pointerId) return;
        if (!moved && Math.abs(e.clientY - startY) < 4) return;
        if (!moved) {
          moved = true;
          reordering = true;
          node.classList.remove('cpt-rail-jiggle');
          node.style.zIndex = '3';
          node.style.cursor = 'grabbing';
          node.style.opacity = '';
          node.style.boxShadow = '0 6px 16px -6px rgba(14,17,22,.45)';
        }
        e.preventDefault();
        follow(e.clientY);
      };

      const onEnd = (e: PointerEvent): void => {
        if (e.pointerId !== pointerId) return;
        window.removeEventListener('pointermove', onMove, true);
        window.removeEventListener('pointerup', onEnd, true);
        window.removeEventListener('pointercancel', onEnd, true);
        if (!moved) return;
        reordering = false;
        node.style.transform = '';
        node.style.zIndex = '';
        node.style.cursor = '';
        node.style.boxShadow = '';
        suppressClick = true;
        setTimeout(() => {
          suppressClick = false;
        }, 0);
        if (e.type === 'pointercancel') {
          renderRibbon();
          return;
        }
        // It already sits where it landed: read its place off its neighbours.
        // Above the divider = pinned, before the next pinned button (or the
        // end of the pinned); below = unpinned, before the next one.
        const children = Array.from(ribbon.children) as HTMLElement[];
        const at = children.indexOf(node);
        const dividerAt = children.indexOf(divider);
        const pinned = dividerAt === -1 || at < dividerAt;
        const next = children[at + 1];
        const beforeId = next && next !== divider ? next.dataset.railItem ?? null : null;
        place(id, pinned, beforeId);
      };

      window.addEventListener('pointermove', onMove, true);
      window.addEventListener('pointerup', onEnd, true);
      window.addEventListener('pointercancel', onEnd, true);
    });
    // The grade chip has its own right-click (it's on the rail even when
    // it isn't in the list).
    if (node !== complianceSlot) node.addEventListener('contextmenu', (e: MouseEvent) => showMenu(e, id));
  }

  /* ------------------------------ right-click ---------------------------- */

  const menu = el(
    'div',
    `position:fixed;z-index:${Z + 1};min-width:170px;background:#fff;border-radius:8px;` +
      'box-shadow:0 6px 24px rgba(15,23,42,.18),0 0 0 1px rgba(15,23,42,.06);padding:4px;' +
      `font:13px/1.3 ${SANS};color:${INK};display:none;box-sizing:border-box;`
  );
  menu.setAttribute('role', 'menu');
  root.appendChild(menu);

  function hideMenu(): void {
    menu.style.display = 'none';
  }

  function menuEntry(label: string, onClick: () => void): HTMLElement {
    const entry = el(
      'button',
      'display:block;width:100%;text-align:left;border:0;background:transparent;' +
        `padding:6px 10px;border-radius:5px;color:${INK};cursor:pointer;font:inherit;`
    );
    entry.type = 'button';
    entry.setAttribute('role', 'menuitem');
    entry.textContent = label;
    entry.addEventListener('mouseenter', () => (entry.style.background = CHEV_HOVER));
    entry.addEventListener('mouseleave', () => (entry.style.background = 'transparent'));
    entry.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      hideMenu();
      onClick();
    });
    return entry;
  }

  /** The menu for one button, opening to the left of the rail. `id` null = the Apps button. */
  function showMenu(e: MouseEvent, id: string | null): void {
    e.preventDefault();
    e.stopPropagation();
    hidePeek();
    const entries: HTMLElement[] = [];
    const heading = el(
      'div',
      `padding:4px 10px 6px;font-size:11px;font-weight:700;color:${MUTED};` +
        'text-transform:uppercase;letter-spacing:.04em;'
    );
    heading.textContent = id ? labelFor(id) : 'Apps';
    entries.push(heading);
    if (id && !LOCKED_ITEMS.includes(id)) {
      const isPinned = resolveRailLayout(layout, availableItems()).pinned.includes(id);
      entries.push(
        menuEntry(isPinned ? 'Unpin — move to Apps' : 'Pin to tabs & rail', () =>
          place(id, !isPinned, null)
        )
      );
    }
    entries.push(menuEntry(editing ? 'Done editing' : 'Edit layout…', () => setEditing(!editing)));
    // The extension's rail can be switched off from itself (the embed's is
    // how its panel opens, so it can't). It comes back from the side panel's
    // edit mode, or Note Preferences.
    const settings = rootId === ROOT_ID ? storageArea('sync') : null;
    if (settings) {
      entries.push(
        menuEntry('Hide ribbon', () => {
          try {
            settings.set({ [RAIL_SETTING_KEY]: false });
          } catch (_) {
            /* the setting is unreachable; the minimise handle still works */
          }
        })
      );
    }
    menu.replaceChildren(...entries);
    menu.style.display = 'block';
    const width = menu.offsetWidth || 170;
    const height = menu.offsetHeight || 80;
    menu.style.left = `${Math.max(8, root.getBoundingClientRect().left - width - 6)}px`;
    menu.style.top = `${Math.min(Math.max(8, e.clientY - 12), window.innerHeight - height - 8)}px`;
  }

  appsButton.addEventListener('contextmenu', (e: MouseEvent) => showMenu(e, null));
  if (!action) complianceSlot.addEventListener('contextmenu', (e: MouseEvent) => showMenu(e, 'compliance'));
  doneButton.addEventListener('click', (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setEditing(false);
  });
  document.addEventListener(
    'pointerdown',
    (e: PointerEvent) => {
      if (menu.contains(e.target as Node)) return;
      hideMenu();
      // A click anywhere off the rail ends edit mode, as on a home screen.
      if (editing && !root.contains(e.target as Node)) setEditing(false);
    },
    true
  );
  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    hideMenu();
    if (editing) setEditing(false);
  });

  // Capture phase, so it also covers the grade slot's own click handler. In
  // edit mode a click on a button only ever means the badge on it.
  ribbon.addEventListener(
    'click',
    (e: MouseEvent) => {
      if (!suppressClick && !editing) return;
      if (editing && !suppressClick && (e.target as HTMLElement)?.closest?.('[data-rail-badge]')) return;
      e.preventDefault();
      e.stopPropagation();
    },
    true
  );

  appsButton.addEventListener('click', (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    hidePeek();
    void openVisitTab('apps');
  });

  // The tab layout, the app catalog and the open tab. The extension's rail
  // reads them from chrome.storage.local, which the side panel writes. The
  // embed's has no extension storage: it asks its panel (an iframe, through
  // loader.js) and hears every change as a WORKSPACE_PUSH.
  // A vendor app's rail has no tabs to mirror.
  const workspaceStorage = action ? null : storageArea('local');

  function applyWorkspace(key: string, value: any): void {
    if (key === 'layout') {
      layout = value ?? null;
      layoutOwner = typeof layout?.owner === 'string' ? layout.owner : null;
      renderRibbon();
    } else if (key === 'active') {
      activeId = typeof value?.id === 'string' ? value.id : null;
      markActive();
    } else if (key === 'catalog') {
      catalog = Array.isArray(value?.items) ? value.items : [];
      // An app's name, colour or icon may have changed: rebuild its button.
      Array.from(itemButtons.keys())
        .filter((id) => id.startsWith('app:'))
        .forEach((id) => itemButtons.delete(id));
      renderRibbon();
    }
  }

  if (action) {
    /* nothing to load */
  } else if (workspaceStorage) {
    void Promise.all([
      readArea<any>('local', LAYOUT_KEY),
      readArea<any>('local', CATALOG_KEY),
      readArea<any>('local', ACTIVE_KEY)
    ]).then(([storedLayout, storedCatalog, storedActive]) => {
      applyWorkspace('catalog', storedCatalog);
      applyWorkspace('active', storedActive);
      applyWorkspace('layout', storedLayout);
    });
    try {
      chrome.storage?.onChanged?.addListener((changes, area) => {
        if (area !== 'local') return;
        if (changes[LAYOUT_KEY]) applyWorkspace('layout', changes[LAYOUT_KEY].newValue);
        if (changes[ACTIVE_KEY]) applyWorkspace('active', changes[ACTIVE_KEY].newValue);
        if (changes[CATALOG_KEY]) applyWorkspace('catalog', changes[CATALOG_KEY].newValue);
      });
    } catch (_) {
      /* no extension runtime: the defaults stand */
    }
  } else {
    void host.ask<any>({ type: 'WORKSPACE_STATE' }).then((state) => {
      if (!state || state.error) return; // an older panel: the defaults stand
      applyWorkspace('catalog', state.catalog);
      applyWorkspace('active', state.active);
      applyWorkspace('layout', state.layout);
    });
    host.listen((request: any) => {
      if (request?.type === 'WORKSPACE_PUSH') applyWorkspace(request.key, request.value);
    });
  }

  applyTrayed();
  renderGrade();
  renderChevron();
  renderRibbon();

  /* -------------------------------- peek ------------------------------- */

  function showPeek(target?: HTMLElement): void {
    if (trayed || editing || reordering) return;
    if (target) peekFor = target;
    if (!peekFor) return;
    clearTimeout(peekTimer);
    peekTimer = setTimeout(() => {
      if (!peekFor) return;
      renderPeek();
      // Beside the button, centred on it. Layout offsets, not screen rects:
      // an EMR page that sets CSS zoom would scale the rects but not `top`.
      let offset = 0;
      let node: HTMLElement | null = peekFor;
      while (node && node !== root) {
        offset += node.offsetTop;
        node = node.offsetParent as HTMLElement | null;
      }
      peek.style.top = `${offset + peekFor.offsetHeight / 2}px`;
      peek.style.opacity = '1';
    }, PEEK_IN_MS);
  }

  function hidePeek(): void {
    clearTimeout(peekTimer);
    peekTimer = setTimeout(() => {
      peek.style.opacity = '0';
      peekFor = null;
    }, PEEK_OUT_MS);
  }

  /** Every rail control gets the tooltip; hovering between them just moves it. */
  function withTip(node: HTMLElement): void {
    node.addEventListener('mouseenter', () => showPeek(node));
    node.addEventListener('mouseleave', () => hidePeek());
  }
  [mark, chevron, complianceSlot, appsButton, doneButton].forEach(withTip);
  mark.dataset.tip = action ? 'Comprehend' : 'Open Comprehend';

  rail.addEventListener('mouseenter', () => {
    minimize.style.opacity = '1';
  });
  rail.addEventListener('mouseleave', () => {
    minimize.style.opacity = '0';
    hidePeek();
  });

  minimize.addEventListener('click', (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    hideComplianceModal();
    setTrayed(true);
  });

  /* ------------------------------ dragging ----------------------------- */

  // Free vertical movement along the edge, matching the embed's toggle
  // (cx_embed loader.js enableToggleDrag): pointer events with pointer capture,
  // an offset so the rail doesn't jump to the cursor, clamped clear of both
  // edges, and persisted on release.
  //
  // Note this departs from ADR-001's three fixed stops -- the embed's free
  // vertical placement is what was asked for. The rail is still docked to the
  // edge; only the vertical position is continuous.
  let dragPointerId: number | null = null;
  let dragOffsetY = 0;

  grip.addEventListener('pointerdown', (e: PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragPointerId = e.pointerId;
    dragOffsetY = e.clientY - root.getBoundingClientRect().top;
    grip.style.cursor = 'grabbing';
    try {
      grip.setPointerCapture(e.pointerId);
    } catch (_) {
      /* capture is a nicety; the move handler still works without it */
    }
  });

  grip.addEventListener('pointermove', (e: PointerEvent) => {
    if (dragPointerId === null || e.pointerId !== dragPointerId) return;
    top = clampTop(e.clientY - dragOffsetY);
    root.style.top = `${top}px`;
  });

  function endDrag(e?: PointerEvent): void {
    if (dragPointerId === null) return;
    if (e && e.pointerId !== dragPointerId) return;
    if (e) {
      try {
        grip.releasePointerCapture(e.pointerId);
      } catch (_) {
        /* already released */
      }
    }
    dragPointerId = null;
    grip.style.cursor = 'grab';
    if (top !== null) writeStored(TOP_KEY, top);
  }

  grip.addEventListener('pointerup', endDrag as EventListener);
  grip.addEventListener('pointercancel', endDrag as EventListener);
  window.addEventListener('blur', () => endDrag());

  window.addEventListener('resize', applyPlacement);

  /* --------------------------- patient switching ----------------------- */

  async function currentNoteKey(): Promise<string | null> {
    try {
      const injector = getInjector();
      return injector ? await readNoteFingerprint(injector) : null;
    } catch (_) {
      return null;
    }
  }

  const notePoll = setInterval(async () => {
    // Hidden by the setting: this rail is gone, so stop watching for it.
    if (!root.isConnected) {
      clearInterval(notePoll);
      return;
    }
    // Nothing to blank without a grade, so don't read the EMR on a timer for it.
    if (scoring || !gradedNoteKey) return;
    const key = await currentNoteKey();
    // A different note means this grade is not about what's on screen any more.
    if (gradedNoteKey && key !== gradedNoteKey) {
      grade = null;
      reasons = [];
      gradedNoteKey = null;
      conversationId = null;
      visitNoteKey = null;
      hideComplianceModal();
      renderGrade();
    }
  }, NOTE_POLL_MS);

  /* -------------------------------- actions ---------------------------- */

  // Chevron opens the real Comprehend side panel, not an in-page panel. When we
  // know which visit this note belongs to, the panel opens straight to it, on
  // the requested tab when there is one.
  function openPanel(tab?: LandingTab): void {
    panelOpen = true;
    renderChevron();
    host.notify({
      type: 'EXPAND_SIDE_PANEL',
      timestamp: Date.now(),
      ...(conversationId
        ? { conversationID: conversationId, ...(tab ? { tab } : {}) }
        : {})
    });
  }

  /** A graded note opens its visit on the Compliance tab, where the grade is. */
  function gradeLandingTab(): LandingTab | undefined {
    return grade && grade !== '--' ? 'compliance' : undefined;
  }

  chevron.addEventListener('click', (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    hidePeek();
    if (panelOpen) {
      panelOpen = false;
      renderChevron();
      host.notify({ type: 'CLOSE_SIDE_PANEL', timestamp: Date.now() });
      return;
    }
    openPanel(gradeLandingTab());
  });

  // Every ribbon button but the grade opens its tab on a visit. Without one
  // yet, the worker resolves it the same way a score would (this tab's open
  // visit, today's visit for this patient, or a new compliance shell) -- it
  // just doesn't grade anything.

  /** No stored session. The panel's login screen is the fix, so open it and say why. */
  function requestSignIn(message: string): void {
    if (!panelOpen) openPanel();
    showComplianceModal({ errorMessage: message });
  }

  /**
   * Signed in is the gate for scoring. Asked before the note is read: that read
   * can take seconds, and chrome.sidePanel.open only works while the click is
   * still recent, so a "sign in" answer has to come back first. A missing
   * answer (worker unreachable) falls through; the score call reports it.
   */
  async function ensureSignedIn(): Promise<boolean> {
    const session = await host.ask<any>({ type: 'FREE_COMPLIANCE_CHECK_SESSION' });
    if (session?.signInRequired) {
      requestSignIn(String(session.error));
      return false;
    }
    return true;
  }

  async function openVisitTab(tab: LandingTab): Promise<void> {
    if (resolvingVisit) return;
    // Open the panel now, while this is still the click: chrome.sidePanel.open
    // needs a user gesture, and reading the note or resolving the visit can
    // outlive one. The openPanel below then moves the open panel to the visit.
    if (!panelOpen) openPanel();
    const confirmed =
      conversationId !== null && visitNoteKey !== null && visitNoteKey === (await currentNoteKey());
    if (!confirmed) {
      resolvingVisit = true;
      ribbon.style.opacity = '.5';
      appsButton.style.opacity = '.5';
      try {
        const note = await readNoteContext();
        const response = await host.ask<any>({
          type: 'FREE_COMPLIANCE_RESOLVE_VISIT',
          payload: notePayload(note)
        });
        if (!response) throw new Error(`Comprehend is not reachable: ${host.error()}`);
        if (response.signInRequired) {
          requestSignIn(String(response.error));
          return;
        }
        if (response.error) throw new Error(String(response.error));
        if (!response.conversationId) {
          throw new Error('Could not open a visit for this note.');
        }
        conversationId = response.conversationId;
        visitNoteKey = note.noteKey;
      } catch (error: any) {
        showComplianceModal({
          errorMessage: error?.message || 'Could not open this visit in Comprehend right now.'
        });
        return;
      } finally {
        resolvingVisit = false;
        ribbon.style.opacity = '1';
        appsButton.style.opacity = '1';
      }
    }
    openPanel(tab);
  }

  // The panel can also be opened or closed from outside the rail (the toolbar
  // icon, the panel's own close button), so follow the broadcasts rather than
  // trusting our own last click. The panel also reports grades it produces, so
  // the rail shows the same grade the clinician just saw there.
  try {
    host.listen((request: any) => {
      if (request?.type === 'SIDE_PANEL_OPENED' && !panelOpen) {
        panelOpen = true;
        renderChevron();
      } else if (request?.type === 'SIDE_PANEL_CLOSED' && panelOpen) {
        panelOpen = false;
        renderChevron();
      } else if (request?.type === 'FREE_COMPLIANCE_MIRROR_PANEL_SCORE') {
        // The worker nests the payload under args. Accept a flat message too,
        // so a panel and a worker from different builds still agree.
        void adoptPanelGrade(request.args || request);
      }
    });
  } catch (_) {
    /* no extension runtime — the chevron still works as a one-way open */
  }

  /** A grade the side panel produced for the note on screen. */
  async function adoptPanelGrade(args: any): Promise<void> {
    // Our own score is in flight; its answer is the newer one.
    if (scoring) return;
    const score = Number(args.score);
    if (!Number.isFinite(score) || score <= 0) {
      log(`ignoring a panel grade with no usable score: ${JSON.stringify(args?.score)}`);
      return;
    }
    // A rail that already established its visit keeps it: a grade for some
    // other visit is not about this note, and adopting it would rebind the
    // next score to that visit. A rail with no visit yet takes the panel's,
    // which is the common case when the panel was opened from the chevron.
    if (conversationId !== null && Number(args.conversationID) !== Number(conversationId)) {
      log(
        `ignoring a panel grade for visit ${JSON.stringify(args.conversationID)}; ` +
          `this rail's visit is ${JSON.stringify(conversationId)}`
      );
      return;
    }
    const noteKey = await currentNoteKey();
    if (scoring) return;
    conversationId = args.conversationID ?? conversationId;
    grade = scoreToGrade(score);
    reasons = Array.isArray(args.requirement_evaluations)
      ? args.requirement_evaluations
      : [];
    gradedNoteKey = noteKey;
    log(`took grade ${grade} from the side panel`);
    renderGrade();
    renderPeek();
  }

  // The score slot does both jobs: unscored, it grades the note; graded, it
  // opens the review. Re-checking lives in the modal, so one slot never has to
  // mean two different things at once.
  gradeBtn.addEventListener('click', (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    hidePeek();
    // In edit mode the chip is being arranged, not asked for a grade.
    if (editing) return;
    if (action) {
      void runAction();
      return;
    }
    if (!grade || grade === '--') {
      void runScore();
      return;
    }
    openReview();
  });

  tray.addEventListener('click', (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setTrayed(false);
  });

  // Cmd/Ctrl+Shift+C restores from the tray and opens the review.
  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (!e.shiftKey || !(e.metaKey || e.ctrlKey)) return;
    if ((e.key || '').toLowerCase() !== 'c') return;
    e.preventDefault();
    if (trayed) {
      setTrayed(false);
      return;
    }
    openReview();
  });

  // The mark is the identity, so clicking it opens Comprehend -- same as the
  // chevron sitting directly beneath it.
  mark.addEventListener('click', (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    hidePeek();
    // No panel to open from a vendor app's rail; the mark is only identity.
    if (action || panelOpen) return;
    openPanel(gradeLandingTab());
  });

  /** The vendor app's action, with the grade's spinner while it runs. */
  async function runAction(): Promise<void> {
    if (!action || scoring || action.blocked?.()) return;
    scoring = true;
    renderGrade();
    renderPeek();
    try {
      await action.run();
    } catch (error: any) {
      showComplianceModal({ errorMessage: error?.message || `${action.title} didn’t work. Try again.` });
    } finally {
      scoring = false;
      renderGrade();
      renderPeek();
    }
  }

  if (action) {
    railRefresh.set(root, () => {
      renderGrade();
      if (peekFor) renderPeek();
    });
  }

  /** The review modal, with a re-check that scores again in place. */
  function openReview(): void {
    if (!grade || grade === '--') return;
    showComplianceModal({
      grade,
      reasons,
      onRecheck: () => void runScore()
    });
  }

  /** Everything the worker needs to know about the note on screen. Throws when
   *  there is no gradeable note here, with the message the modal should show. */
  async function readNoteContext(): Promise<{
    noteKey: string | null;
    pageInfo: any;
    questions: Record<string, any>;
    patientInfo: any;
    emrMetadata: any;
  }> {
    const injector = getInjector();
    if (!canReadNote(injector)) {
      throw new Error('This page is not a note we can grade.');
    }

    const noteKey = await currentNoteKey();
    const pageInfo = await readPageInfo(injector!);
    if (!pageInfo?.['URL'] || !pageInfo?.['Note Type']) {
      throw new Error('Open the patient note in your EMR, then try again.');
    }

    const questions = await readNoteValues(injector!);
    if (!questions || Object.keys(questions).length === 0) {
      throw new Error('No note content found on this page yet.');
    }

    const patientInfo = await readPatientInfo(injector!);

    let emrMetadata: any = null;
    try {
      emrMetadata = injector!.getPageMetadata
        ? await Promise.resolve(injector!.getPageMetadata())
        : null;
    } catch (_) {
      emrMetadata = null;
    }

    return { noteKey, pageInfo, questions, patientInfo, emrMetadata };
  }

  /** The part of the payload that identifies the note and its visit. */
  function notePayload(note: {
    pageInfo: any;
    questions: Record<string, any>;
    patientInfo: any;
  }): Record<string, any> {
    return {
      pageInfo: {
        URL: note.pageInfo['URL'],
        'Note Type': note.pageInfo['Note Type']
      },
      sections: Object.keys(note.questions),
      patientInfo: note.patientInfo || null,
      conversationId
    };
  }

  async function runScore(): Promise<void> {
    if (scoring) return;
    scoring = true;
    renderGrade();
    renderPeek();

    try {
      if (!(await ensureSignedIn())) {
        scoring = false;
        renderGrade();
        renderPeek();
        return;
      }

      const note = await readNoteContext();

      const response = await host.ask<any>({
        type: 'FREE_COMPLIANCE_SCORE',
        payload: {
          ...notePayload(note),
          existingValues: note.questions,
          emrMetadata:
            note.emrMetadata && typeof note.emrMetadata === 'object'
              ? note.emrMetadata
              : null
        }
      });

      if (!response) throw new Error(`Comprehend is not reachable: ${host.error()}`);
      if (response.signInRequired) {
        scoring = false;
        renderGrade();
        renderPeek();
        requestSignIn(String(response.error));
        return;
      }
      if (response.error) throw new Error(String(response.error));

      conversationId = response.conversationId ?? conversationId;
      visitNoteKey = note.noteKey;
      grade = scoreToGrade(response.score);
      reasons = Array.isArray(response.requirement_evaluations)
        ? response.requirement_evaluations
        : [];
      gradedNoteKey = note.noteKey;
      scoring = false;
      renderGrade();
      renderPeek();
      // No auto-open: the grade lands in the rail and waits to be asked about.
    } catch (error: any) {
      scoring = false;
      renderGrade();
      renderPeek();
      showComplianceModal({
        errorMessage: error?.message || 'Could not check compliance right now.'
      });
    }
  }

  return root;
}
